import { z } from 'zod';

/**
 * Numeric values accepted by stock-load gateways.
 *
 * Workflow template variables are resolved to strings at runtime, while MCP
 * and HTTP callers normally send numbers. Coercion is deliberately limited to
 * non-empty numeric strings so an empty value can never become zero.
 */
const finiteNumber = z.union([
  z.number().finite(),
  z.string()
    .trim()
    .min(1, 'Il valore numerico non può essere vuoto')
    .refine((value) => Number.isFinite(Number(value)), 'Numero non valido')
    .transform(Number),
]);

const positiveQuantity = finiteNumber
  .refine((value) => value > 0, 'La quantità deve essere maggiore di zero');

const nonNegativeUnitCost = finiteNumber
  .refine((value) => value >= 0, 'Il costo unitario non può essere negativo');

const nullableUnitCost = z.preprocess(
  (value) => typeof value === 'string' && value.trim() === '' ? null : value,
  nonNegativeUnitCost.nullish(),
);

/**
 * Canonical payload for a stock load.
 *
 * `unit_cost` is the net unit purchase cost (discount already applied). It is
 * nullable/optional at the boundary because a load may legitimately have an
 * unknown cost; callers should normalize a missing value to null when
 * forwarding it to the ledger or returning it to the caller.
 */
export const StockLoadSchema = z.object({
  product_id: z.string().min(1).max(255),
  warehouse_id: z.string().min(1).max(255),
  lot_id: z.string().max(255).nullish(),
  lot_number: z.string().max(255).nullish(),
  variant_id: z.string().uuid().nullish(),
  supplier_id: z.string().uuid().nullish(),
  supplier_sku: z.string().trim().min(1).max(255).nullish(),
  quantity: positiveQuantity,
  unit_cost: nullableUnitCost,
  serial_number: z.string().max(1024).nullish(),
  notes: z.string().max(5000).nullish(),
  created_by: z.string().max(255).nullish(),
}).strict();

export type StockLoadInput = z.infer<typeof StockLoadSchema>;

/**
 * Canonical payload for the legacy single-line stock transfer gateways.
 *
 * Keep this contract separate from the document transfer state machine: these
 * fields are only the arguments needed by `PosStockService.transferStock`.
 * `variant_id` is nullable because products without variants remain valid.
 */
export const StockTransferSchema = z.object({
  product_id: z.string().min(1).max(255),
  from_warehouse_id: z.string().min(1).max(255),
  to_warehouse_id: z.string().min(1).max(255),
  lot_id: z.string().max(255).nullish(),
  variant_id: z.string().uuid().nullish(),
  quantity: positiveQuantity,
  notes: z.string().max(5000).nullish(),
  // Retained for the legacy HTTP audit action; it is not a service state.
  requires_approval: z.boolean().nullish(),
}).strict();

export type StockTransferInput = z.infer<typeof StockTransferSchema>;
/**
 * Canonical payload for a customer return re-entering stock
 * (`POST /movements/return`).
 *
 * The quantity is strictly positive: a return can only ADD stock. Any
 * reduction must go through an adjustment (reason + `adjustment.create`).
 * Strict by design: unknown fields are rejected at the boundary.
 */
export const StockReturnSchema = z.object({
  product_id: z.string().min(1).max(255),
  warehouse_id: z.string().min(1).max(255),
  lot_id: z.string().max(255).nullish(),
  quantity: positiveQuantity,
  // Resi/manual entry passano già il costo netto: nessuno sconto documentale.
  unit_cost: nullableUnitCost,
  reason: z.string().max(5000).nullish(),
  notes: z.string().max(5000).nullish(),
}).strict();

export type StockReturnInput = z.infer<typeof StockReturnSchema>;

/**
 * Vocabolario delle motivazioni di rettifica: identico a quello delle righe
 * di inventario (`pos_inventory_session_items.adjustment_reason`, CHECK
 * `pos_inv_items_adj_reason_check`) e persistito anche sul movimento di
 * rettifica manuale (`pos_stock_movements.adjustment_reason`).
 */
export const STOCK_ADJUSTMENT_REASONS = ['furto', 'rottura', 'errore_carico', 'scaduto', 'smarrito', 'altro'] as const;
export const stockAdjustmentReasonSchema = z.enum(STOCK_ADJUSTMENT_REASONS);
export type StockAdjustmentReason = z.infer<typeof stockAdjustmentReasonSchema>;

export const STOCK_ADJUSTMENT_REASON_LABELS: Readonly<Record<StockAdjustmentReason, string>> = {
  furto: 'Furto',
  rottura: 'Rottura',
  errore_carico: 'Errore carico',
  scaduto: 'Scaduto',
  smarrito: 'Smarrito',
  altro: 'Altro',
};

const nonNegativeQuantity = finiteNumber
  .refine((value) => value >= 0, 'La quantità non può essere negativa');

/**
 * Canonical payload for a manual stock adjustment (`POST /movements/adjustment`,
 * MCP `stock_adjustment`, workflow `action_stock_adjustment`).
 *
 * `quantity` is the ABSOLUTE target quantity of the slot (legacy semantic).
 * `adjustment_reason` is mandatory: a manual adjustment without a reason is
 * rejected at every entry point and the reason is persisted on the movement.
 */
export const StockAdjustmentSchema = z.object({
  product_id: z.string().min(1).max(255),
  warehouse_id: z.string().min(1).max(255),
  lot_id: z.string().max(255).nullish(),
  quantity: nonNegativeQuantity,
  adjustment_reason: stockAdjustmentReasonSchema,
  notes: z.string().max(5000).nullish(),
}).strict();

export type StockAdjustmentInput = z.infer<typeof StockAdjustmentSchema>;

/**
 * Codice errore condiviso (403) quando un magazzino indirizzato da una lettura
 * di dettaglio o da una scrittura è fuori dal recinto dell'utente
 * (`user_scopes` ∪ allargamenti attivi → sedi → magazzini).
 */
export const WAREHOUSE_OUT_OF_SCOPE_CODE = 'WAREHOUSE_OUT_OF_SCOPE';
